import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { companyCamLinkSignalKey } from "@/react/hooks/entityHooks/useSwivuCompanyCam";

/** Ignore link signals older than this (avoids false positive on dashboard open). */
export const COMPANY_CAM_LINK_STALE_MS = 5_000;
/** Max time to show importing UI / run fast file-list poll after a link. */
export const COMPANY_CAM_IMPORT_TIMEOUT_MS = 30_000;

const FAST_STALE_MS = 10_000;
const FAST_REFETCH_MS = 15_000;
const DEFAULT_STALE_MS = 30_000;
const DEFAULT_REFETCH_MS = 60_000;

/**
 * Tracks a fresh CompanyCam project link for import UI + faster file-list polling.
 *
 * - UI (`isImportingCompanyCam`) clears when totalCount grows or on timeout.
 * - Fast poll stays active for the full timeout so staggered imports keep arriving.
 *
 * Call `reportTotalCount` whenever the entity file list total changes.
 */
export function useCompanyCamImportState({
  entityType,
  entityId,
}: {
  entityType: string;
  entityId: number;
}) {
  const [isImportingCompanyCam, setIsImportingCompanyCam] = useState(false);
  const [isFastPolling, setIsFastPolling] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const countAtImportStartRef = useRef<number | null>(null);
  const totalCountRef = useRef(0);
  totalCountRef.current = totalCount;

  const { data: companyCamLinkedAt = 0 } = useQuery({
    queryKey: companyCamLinkSignalKey(entityType, entityId),
    queryFn: () => 0,
    staleTime: Infinity,
    initialData: 0,
  });

  useEffect(() => {
    if (!companyCamLinkedAt) return;
    if (Date.now() - companyCamLinkedAt > COMPANY_CAM_LINK_STALE_MS) return;
    countAtImportStartRef.current = totalCountRef.current;
    setIsImportingCompanyCam(true);
    setIsFastPolling(true);
  }, [companyCamLinkedAt]);

  // Clear importing UI once new files show up; keep fast poll running.
  useEffect(() => {
    if (!isImportingCompanyCam || countAtImportStartRef.current == null) return;
    if (totalCount > countAtImportStartRef.current) {
      setIsImportingCompanyCam(false);
      countAtImportStartRef.current = null;
    }
  }, [totalCount, isImportingCompanyCam]);

  useEffect(() => {
    if (!isFastPolling) return;
    const timer = window.setTimeout(() => {
      setIsImportingCompanyCam(false);
      setIsFastPolling(false);
      countAtImportStartRef.current = null;
    }, COMPANY_CAM_IMPORT_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isFastPolling]);

  return {
    isImportingCompanyCam,
    fileListStaleTime: isFastPolling ? FAST_STALE_MS : DEFAULT_STALE_MS,
    fileListRefetchInterval: isFastPolling ? FAST_REFETCH_MS : DEFAULT_REFETCH_MS,
    reportTotalCount: setTotalCount,
  };
}
